import { injectable } from '../shared/inversify';

export const OpenService = Symbol.for('OpenService');

export interface OpenOptions {
    target?: '_blank' | '_self' | '_parent' | '_top';
    noopener?: boolean;
}

/**
 * Represents a service for opening URLs.
 */
export interface OpenService {
    /**
     * Opens the specified URL.
     * @param url The URL to open.
     * @param data Optional data for opening the URL.
     */
    open(url: string, data?: OpenOptions): void;

    /**
     * Asynchronously opens the specified URL.
     * @param url The URL to open.
     * @param data Optional data for opening the URL.
     * @returns A promise that resolves when the URL is opened.
     */
    openAsync(url: string, data?: OpenOptions): Promise<void>;
}

/**
 * Service for opening URLs in a new window or tab.
 */
@injectable()
export class OpenServiceImpl implements OpenService {
    constructor(

    ) {
        // pass
    }

    /**
     * Opens the specified URL in a new window or tab.
     * @param url The URL to open.
     * @param data Optional configuration for opening the URL.
     */
    open(url: string, data: OpenOptions = {}): void {
        window.open(url, data.target ?? '_blank', data.noopener ? 'noopener' : '');
    }

    /**
     * Opens the specified URL asynchronously in a new window or tab.
     * @param url The URL to open.
     * @param data Optional configuration for opening the URL.
     * @returns A promise that resolves when the URL is successfully opened.
     */
    openAsync(url: string, data: OpenOptions = {}): Promise<void> {
        return new Promise((resolve, reject) => {
            const tab = window.open();
            try {
                if (data.noopener) {
                    tab!.opener = null;
                }
                tab!.location = url;
                resolve();
            } catch (error) {
                reject(error);
            }
        });
    }
}
